import Decimal from 'decimal.js-light';

// The product search on a warehouse patch shows at most this many products.
export const PRODUCT_SEARCH_LIMIT = 50;

interface ILine {
  id: string | null;
  quantity: Decimal;
  checkpoint: boolean;
  _destroy: boolean;
}

// The product search adds its hits as inactive lines: greyed out and
// disabled until the user activates one. Activating it makes its count,
// zero included, a deliberate one (fakturabank#2636 item 32).
interface IActivatableLine extends ILine {
  inactive?: boolean;
}

// A line with no quantity that is not a full count changes nothing in the warehouse.
export function lineAffectsNothing(line: Pick<ILine, 'quantity' | 'checkpoint'>): boolean {
  return new Decimal(line.quantity || 0).isZero() && !line.checkpoint;
}

export function isInactiveLine(line: Pick<IActivatableLine, 'id' | 'inactive'>): boolean {
  return !!line.inactive && !line.id;
}

// The lines a new search removes: the hits the previous search added that
// the user never activated. (A change of supplier removes every new line
// with quantity zero, these included.)
export function inactiveLines<T extends IActivatableLine>(lines: T[]): T[] {
  return lines.filter(line => isInactiveLine(line));
}

// From now on the line is an ordinary line.
export function activateLine(line: IActivatableLine): void {
  line.inactive = false;
}

// The lines to send on save: inactive lines are never sent, and lines that
// affect nothing are left out, or destroyed when they were saved before.
export function linesToSave<T extends IActivatableLine>(lines: T[]): T[] {
  return lines
    .filter(line => !isInactiveLine(line))
    .filter(line => line.id || !lineAffectsNothing(line))
    .map(line => (line.id && !line._destroy && lineAffectsNothing(line) ? { ...line, _destroy: true } : line));
}

// A patch is a full count ("Fullstendig varetelling") only when every line is one.
export function isFullCountPatch(lines: Pick<ILine, 'checkpoint'>[]): boolean {
  return lines.length > 0 && lines.every(line => line.checkpoint);
}
